if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia("(min-width: 750px)");

        if (this.classList.contains("media-gallery--thumbs"))
          this.initThumbGallery();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener(
          "slideChanged",
          debounce(this.onSlideChanged.bind(this), 150)
        );
        this.elements.thumbnails
          .querySelectorAll("[data-target]")
          .forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector("button")
              .addEventListener(
                "click",
                this.setActiveMedia.bind(this, mediaToSwitch.dataset.target)
              );
          });
        if (
          this.dataset.desktopLayout.includes("thumbnail") &&
          this.mql.matches
        )
          this.removeListSemantic();
      }

      initThumbGallery() {
        this.elements.counterCurrent = this.querySelector("[data-gallery-current]");
        this.elements.counterTotal = this.querySelector("[data-gallery-total]");

        this.querySelectorAll("[data-gallery-step]").forEach((button) => {
          button.addEventListener("click", () =>
            this.stepMedia(Number(button.dataset.galleryStep))
          );
        });

        this.querySelector("[data-gallery-zoom]")?.addEventListener("click", () => {
          const activeThumbnail = this.getVisibleThumbnails().find((item) =>
            item.querySelector("button[aria-current]")
          );
          const activeMedia =
            (activeThumbnail &&
              this.elements.viewer.querySelector(
                `[data-media-id="${activeThumbnail.dataset.target}"]`
              )) ||
            this.elements.viewer.querySelector("[data-media-id]");
          activeMedia?.querySelector(".product__media-toggle")?.click();
        });

        this.initThumbnailLines();
        this.updateCounter();
      }

      getVisibleThumbnails() {
        if (!this.elements.thumbnails) return [];

        return Array.from(
          this.elements.thumbnails.querySelectorAll("[data-target]")
        ).filter((item) => getComputedStyle(item).display !== "none");
      }

      stepMedia(direction) {
        const thumbnails = this.getVisibleThumbnails();
        if (thumbnails.length < 2) return;
        const current = thumbnails.findIndex((item) =>
          item.querySelector("button[aria-current]")
        );
        const next =
          (Math.max(current, 0) + direction + thumbnails.length) %
          thumbnails.length;
        // Jump straight to the other end when wrapping instead of scrolling past every slide
        const wrapped = Math.abs(next - current) > 1;
        const viewerSlider = this.elements.viewer.slider;
        if (wrapped && viewerSlider) {
          viewerSlider.style.scrollBehavior = "auto";
          setTimeout(() => viewerSlider.style.removeProperty("scroll-behavior"), 100);
        }
        this.setActiveMedia(thumbnails[next].dataset.target, false);
      }

      initThumbnailLines() {
        if (!this.elements.thumbnails) return;

        const container = this.querySelector("[data-thumbnail-lines]");
        // Query directly - on an AJAX section swap the thumbnail slider-component isn't upgraded yet
        const strip = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails"]');
        if (!container || !strip) return;

        let pageOffsets = [];

        const getPageOffsets = () => {
          const thumbnails = this.getVisibleThumbnails();
          if (!thumbnails.length || !strip.clientWidth) return [];

          const firstThumbnail = thumbnails[0];
          const secondThumbnail = thumbnails[1];
          let thumbnailsPerPage = 1;

          if (secondThumbnail) {
            const firstRect = firstThumbnail.getBoundingClientRect();
            const secondRect = secondThumbnail.getBoundingClientRect();
            const thumbnailStep = secondRect.left - firstRect.left;
            const gap = Math.max(0, thumbnailStep - firstRect.width);

            if (thumbnailStep > 0) {
              thumbnailsPerPage = Math.max(
                1,
                Math.round((strip.clientWidth + gap) / thumbnailStep)
              );
            }
          }

          const maxScroll = Math.max(0, strip.scrollWidth - strip.clientWidth);
          const pageCount = Math.ceil(thumbnails.length / thumbnailsPerPage);

          return Array.from({ length: pageCount }, (_, index) => {
            const thumbnail = thumbnails[index * thumbnailsPerPage];
            const offset = thumbnail.offsetLeft - firstThumbnail.offsetLeft;
            return Math.min(offset, maxScroll);
          });
        };

        const setActive = () => {
          const lines = container.children;
          if (!lines.length || !pageOffsets.length) return;
          const index = pageOffsets.reduce(
            (closestIndex, offset, offsetIndex) =>
              Math.abs(offset - strip.scrollLeft) <
              Math.abs(pageOffsets[closestIndex] - strip.scrollLeft)
                ? offsetIndex
                : closestIndex,
            0
          );
          Array.from(lines).forEach((line, i) =>
            line.classList.toggle("is-active", i === index)
          );
        };

        const render = () => {
          pageOffsets = getPageOffsets();
          const pages = pageOffsets.length;
          container.hidden = pages < 2;
          if (container.children.length !== pages) {
            container.innerHTML = "";
            for (let i = 0; i < pages; i++) {
              const line = document.createElement("button");
              line.type = "button";
              line.className = "thumbnail-lines__line";
              line.setAttribute("aria-label", `Show thumbnails ${i + 1} of ${pages}`);
              line.addEventListener("click", () =>
                strip.scrollTo({ left: pageOffsets[i] ?? 0, behavior: "smooth" })
              );
              container.appendChild(line);
            }
          }
          setActive();
        };

        strip.addEventListener("scroll", setActive, { passive: true });
        new ResizeObserver(render).observe(strip);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);

        // Update counter when slide changes via navigation
        this.updateCounter();
      }

      setActiveMedia(mediaId, scrollIntoView = true) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector("[data-media-id]");
        if (!activeMedia) {
          return;
        }
        this.elements.viewer
          .querySelectorAll("[data-media-id]")
          .forEach((element) => {
            element.classList.remove("is-active");
          });
        activeMedia?.classList?.add("is-active");

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }
          if (!scrollIntoView) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: "smooth" });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset.mediaPosition
        );

        // Update counter after slide change
        this.updateCounter();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));
        thumbnail.querySelector("button").setAttribute("aria-current", true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          ".product__modal-opener--image img"
        );
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              "[index]",
              position
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute("role", "presentation")
        );
      }

      updateCounter() {
        if (this.elements.counterCurrent) {
          const thumbnails = this.getVisibleThumbnails();
          const current = thumbnails.findIndex((item) =>
            item.querySelector("button[aria-current]")
          );
          this.elements.counterCurrent.textContent = Math.max(current, 0) + 1;
          this.elements.counterTotal.textContent = thumbnails.length;
          return;
        }

        // Trigger the slider's update method to refresh the counter
        if (this.elements.viewer && this.elements.viewer.update) {
          this.elements.viewer.update();
        }
      }
    }
  );
}
